import type { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/atoms/dialog';
import { WisetackSettingsForm } from '@/components/settings/WisetackSettingsForm';
import type { WisetackSettings } from '@/types/settings';

/**
 * Wisetack is the one integration whose card can't hold its own controls —
 * the enrollment panel carries a copyable prequalification link plus three
 * portal deep-links, which is a card-and-a-half of content on its own.
 *
 * Putting it in a dialog keeps every affordance exactly as it was (this is
 * the same `WisetackSettingsForm` the page has always rendered) while letting
 * the Wisetack card stay the same height as the seven beside it.
 */
export function WisetackManageDialog({
  trigger,
  settings,
  isLoading = false,
}: {
  trigger: ReactNode;
  settings: WisetackSettings | undefined;
  /**
   * Must reflect the parent query. `WisetackSettingsForm` falls back to
   * WISETACK_DEFAULT_SETTINGS when `settings` is undefined, and that default
   * carries `enabled: false` — which renders "Wisetack is not available for
   * this company". Hardcoding `isLoading={false}` therefore tells an approved
   * tenant they have no financing at all, for as long as the fetch is in
   * flight. The form renders a skeleton instead when this is true.
   */
  isLoading?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Wisetack financing</DialogTitle>
          <DialogDescription>
            Enrollment status, your prequalification link, and the Wisetack merchant portal.
          </DialogDescription>
        </DialogHeader>
        <WisetackSettingsForm settings={settings} isLoading={isLoading} />
      </DialogContent>
    </Dialog>
  );
}
